import type {
  EngagementScoringSettingsRequest,
  EngagementScoringSettingsResponse,
} from "@/types/generated/cms.generated";

/**
 * Engagement score formula maths (plans/ps-cms-engagement.md T2) — pure, no React.
 *
 * Weights and the slipping floor are stored as FRACTIONS (0.40) and shown as whole PERCENTS (40 %).
 * Comparisons are made in integer percents (tenths for a stored value that is not a whole percent),
 * edited values are sent as `percent / 100` rounded to 4 dp, and a visible field the admin did not
 * edit is sent back exactly as loaded (a stored 0.125 never becomes 0.13). Every configuration field
 * this page does not show is sent back exactly as loaded, so the page never alters what it hides.
 */

/** The ten numbers the page edits, as the text in their boxes. */
export interface EngagementForm {
  challenge: string;
  activity: string;
  consistency: string;
  social: string;
  silver: string;
  gold: string;
  platinum: string;
  graceDays: string;
  windowDays: string;
  /** The lowest point, in percent of the score. */
  floor: string;
}

export type FormField = keyof EngagementForm;
export type Tier = "Bronze" | "Silver" | "Gold" | "Platinum";

export const MESSAGES = {
  weightRange: "Each weight must be between 0 and 100 %.",
  levels: "Levels must go up: Silver below Gold below Platinum, and Platinum at most 100.",
  grace: "The waiting time must be at least 1 day.",
  window: "The slipping time must be at least 1 day.",
  floor: "The lowest point must be between 0 and 100 %.",
} as const;

export const weightsTotalMessage = (total: number) => `Weights must add up to 100 % (now ${formatNumber(total)} %).`;

/** The four weights in display order, with the form field and the request field they map to. */
export const WEIGHT_FIELDS = [
  { field: "challenge", key: "challengeWeight", label: "Challenges" },
  { field: "activity", key: "activityWeight", label: "Activities" },
  { field: "consistency", key: "consistencyWeight", label: "Consistency" },
  { field: "social", key: "socialWeight", label: "Social" },
] as const satisfies ReadonlyArray<{ field: FormField; key: keyof EngagementScoringSettingsRequest; label: string }>;

/** Example scores for the four parts (preview card): 70 · 60 · 80 · 50. */
export const EXAMPLE_SCORES = [70, 60, 80, 50] as const;

const REQUEST_KEYS = [
  "challengeWeight",
  "activityWeight",
  "consistencyWeight",
  "socialWeight",
  "socialFeedWeight",
  "socialProfileWeight",
  "socialFeedbackWeight",
  "silverThreshold",
  "goldThreshold",
  "platinumThreshold",
  "decayGraceDays",
  "decayWindowDays",
  "decayFloor",
  "trendThreshold",
  "socialLogScaleCounts",
  "socialMaxPostsPerDay",
  "socialMaxCommentsPerDay",
  "socialMaxReactionsPerDay",
  "socialMaxKudosPerDay",
  "moderationReportPenalty",
] as const satisfies ReadonlyArray<keyof EngagementScoringSettingsRequest>;

type RequestKey = (typeof REQUEST_KEYS)[number];

/** How each shown field maps onto the request: a fraction shown as a percent, or a plain number. */
const SHOWN: Record<FormField, { key: RequestKey; kind: "fraction" | "points" | "days" }> = {
  challenge: { key: "challengeWeight", kind: "fraction" },
  activity: { key: "activityWeight", kind: "fraction" },
  consistency: { key: "consistencyWeight", kind: "fraction" },
  social: { key: "socialWeight", kind: "fraction" },
  silver: { key: "silverThreshold", kind: "points" },
  gold: { key: "goldThreshold", kind: "points" },
  platinum: { key: "platinumThreshold", kind: "points" },
  graceDays: { key: "decayGraceDays", kind: "days" },
  windowDays: { key: "decayWindowDays", kind: "days" },
  floor: { key: "decayFloor", kind: "fraction" },
};

const FORM_FIELDS = Object.keys(SHOWN) as FormField[];
const SHOWN_KEYS = new Set<RequestKey>(FORM_FIELDS.map((f) => SHOWN[f].key));

/** Whole numbers stay whole ("40"); anything else shows one decimal ("12.5"). */
function formatNumber(value: number): string {
  const tenths = Math.round(value * 10);
  return tenths % 10 === 0 ? String(tenths / 10) : (tenths / 10).toFixed(1);
}

/** A stored fraction as percent text: 0.15 ⇒ "15", 0.125 ⇒ "12.5". */
export function percentText(fraction: number): string {
  return formatNumber(fraction * 100);
}

function fieldText(settings: EngagementScoringSettingsResponse, field: FormField): string {
  const { key, kind } = SHOWN[field];
  const value = settings[key];
  if (typeof value !== "number") return "";
  return kind === "fraction" ? percentText(value) : formatNumber(value);
}

/** The loaded settings as the text of the ten boxes. */
export function toForm(settings: EngagementScoringSettingsResponse): EngagementForm {
  return Object.fromEntries(FORM_FIELDS.map((f) => [f, fieldText(settings, f)])) as unknown as EngagementForm;
}

/** True when the box still holds the loaded value's text (so the loaded value is sent back untouched). */
function unchanged(form: EngagementForm, field: FormField, original?: EngagementScoringSettingsResponse): boolean {
  return !!original && form[field].trim() === fieldText(original, field);
}

/** A whole number typed in a box, else null (empty, not a number, or a decimal). */
function wholeNumber(text: string): number | null {
  const trimmed = text.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * The value of a box in the box's own unit (percent, points or days): the loaded value when the box
 * is unchanged (may be a decimal), else the whole number typed, else null.
 */
function shownValue(form: EngagementForm, field: FormField, original?: EngagementScoringSettingsResponse): number | null {
  if (unchanged(form, field, original)) {
    const raw = original![SHOWN[field].key] as number;
    // Snap float noise (0.15 × 100 = 15.000000000000002) so a whole stored percent stays whole.
    return SHOWN[field].kind === "fraction" ? Math.round(raw * 100 * 1e9) / 1e9 : raw;
  }
  return wholeNumber(form[field]);
}

/** The value to send for a shown field: the loaded value when unchanged, else the typed value. */
function bodyValue(form: EngagementForm, field: FormField, original: EngagementScoringSettingsResponse): number {
  const { key, kind } = SHOWN[field];
  if (unchanged(form, field, original)) return original[key] as number;
  const typed = Number(form[field].trim());
  return kind === "fraction" ? Math.round((typed / 100) * 10000) / 10000 : typed;
}

/** Whether any box differs from the loaded values. */
export function isDirty(form: EngagementForm, original: EngagementScoringSettingsResponse): boolean {
  return FORM_FIELDS.some((f) => !unchanged(form, f, original));
}

/**
 * The PUT / preview body: every configuration field of the loaded settings (never `updatedAt`,
 * `updatedByUserId`, `updatedByName`), with the shown fields replaced by the form's values. Call only
 * with a valid form. Throws when the loaded settings lack a field — a value is never invented.
 */
export function toBody(form: EngagementForm, original: EngagementScoringSettingsResponse): EngagementScoringSettingsRequest {
  const body = {} as Record<RequestKey, number | boolean>;
  for (const key of REQUEST_KEYS) {
    const value = original[key];
    if (value === undefined || value === null) {
      throw new Error(`Engagement settings are missing "${key}".`);
    }
    body[key] = value;
  }
  for (const field of FORM_FIELDS) {
    body[SHOWN[field].key] = bodyValue(form, field, original);
  }
  return body as unknown as EngagementScoringSettingsRequest;
}

/** The weights' total in percent; null when a weight box is not a valid number. */
export function weightsTotal(form: EngagementForm, original?: EngagementScoringSettingsResponse): number | null {
  const values = WEIGHT_FIELDS.map(({ field }) => shownValue(form, field, original));
  if (values.some((v) => v === null)) return null;
  const nums = values as number[];
  if (nums.every((v) => Number.isInteger(v))) return nums.reduce((a, b) => a + b, 0);
  // A stored value that is not a whole percent: total the fractions, rounded to one decimal.
  const fractions = nums.reduce((a, b) => a + b / 100, 0);
  return Math.round(fractions * 1000) / 10;
}

/** Every rule the form breaks, as the messages shown to the admin (empty = valid). */
export function validate(form: EngagementForm, original?: EngagementScoringSettingsResponse): string[] {
  const errors: string[] = [];
  const value = (f: FormField) => shownValue(form, f, original);

  const weights = WEIGHT_FIELDS.map(({ field }) => value(field));
  if (weights.some((w) => w === null || w < 0 || w > 100)) {
    errors.push(MESSAGES.weightRange);
  } else {
    const total = weightsTotal(form, original);
    if (total !== null && total !== 100) errors.push(weightsTotalMessage(total));
  }

  const [silver, gold, platinum] = [value("silver"), value("gold"), value("platinum")];
  if (silver === null || gold === null || platinum === null || !(silver > 0 && silver < gold && gold < platinum && platinum <= 100)) {
    errors.push(MESSAGES.levels);
  }

  const grace = value("graceDays");
  if (grace === null || grace < 1) errors.push(MESSAGES.grace);
  const window = value("windowDays");
  if (window === null || window < 1) errors.push(MESSAGES.window);

  const floor = value("floor");
  if (floor === null || floor < 0 || floor > 100) errors.push(MESSAGES.floor);

  return errors;
}

/** The weight of each part as a fraction (for the example and the bars). */
export function weightFractions(form: EngagementForm, original?: EngagementScoringSettingsResponse): number[] {
  return WEIGHT_FIELDS.map(({ field }) => (shownValue(form, field, original) ?? 0) / 100);
}

/** What each part adds for the 70 · 60 · 80 · 50 example, to one decimal. */
export function exampleParts(form: EngagementForm, original?: EngagementScoringSettingsResponse): number[] {
  return weightFractions(form, original).map((w, i) => Math.round(EXAMPLE_SCORES[i] * w * 10) / 10);
}

/** Whether the local parts add up to the server's example total (else the parts line is hidden). */
export function partsMatchTotal(parts: number[], total: number): boolean {
  const sum = Math.round(parts.reduce((a, b) => a + b, 0) * 10);
  return sum === Math.round(total * 10);
}

/** The level a total reaches under the form's thresholds. */
export function tierFor(total: number, form: EngagementForm): Tier {
  const n = (t: string) => Number(t.trim());
  if (total >= n(form.platinum)) return "Platinum";
  if (total >= n(form.gold)) return "Gold";
  if (total >= n(form.silver)) return "Silver";
  return "Bronze";
}

/** Names used in the History summaries. */
const HISTORY_LABELS: Record<FormField, { label: string; unit: "%" | "days" | "" }> = {
  challenge: { label: "Challenges", unit: "%" },
  activity: { label: "Activities", unit: "%" },
  consistency: { label: "Consistency", unit: "%" },
  social: { label: "Social", unit: "%" },
  silver: { label: "Silver", unit: "" },
  gold: { label: "Gold", unit: "" },
  platinum: { label: "Platinum", unit: "" },
  graceDays: { label: "Waiting time", unit: "days" },
  windowDays: { label: "Slipping time", unit: "days" },
  floor: { label: "Lowest point", unit: "%" },
};

/**
 * One line naming what changed between two saved snapshots ("Challenges 40 % → 35 %, Gold 60 → 65");
 * "Other settings changed" when only hidden configuration fields moved; "" when nothing did.
 * The audit fields (`updatedAt`, `updatedByUserId`, `updatedByName`) are never compared.
 */
export function historySummary(
  previous: EngagementScoringSettingsResponse,
  next: EngagementScoringSettingsResponse
): string {
  const parts: string[] = [];
  // A shown field that moved by less than its printed precision (12.5 % → 12.5 %) counts as "other".
  let otherChanged = false;
  for (const field of FORM_FIELDS) {
    const before = fieldText(previous, field);
    const after = fieldText(next, field);
    if (previous[SHOWN[field].key] === next[SHOWN[field].key]) continue;
    if (before === after) {
      otherChanged = true;
      continue;
    }
    const { label, unit } = HISTORY_LABELS[field];
    if (unit === "%") parts.push(`${label} ${before} % → ${after} %`);
    else if (unit === "days") parts.push(`${label} ${before} → ${after} days`);
    else parts.push(`${label} ${before} → ${after}`);
  }
  const hiddenChanged = REQUEST_KEYS.some((key) => !SHOWN_KEYS.has(key) && previous[key] !== next[key]);
  if (hiddenChanged || otherChanged) parts.push("Other settings changed");
  return parts.join(", ");
}

const UAE_OFFSET_MS = 4 * 60 * 60 * 1000; // UTC+4, no daylight saving
const NIGHTLY_RUN_UTC_HOUR = 3; // 03:00 UTC = 07:00 UAE (EngagementSchedule.NightlyRunUtc)
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** The next nightly run strictly after `now`. */
export function nextNightlyRun(now: Date): Date {
  const run = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), NIGHTLY_RUN_UTC_HOUR));
  if (run.getTime() <= now.getTime()) run.setTime(run.getTime() + DAY_MS);
  return run;
}

/** UAE calendar day number of an instant (days since the epoch in UAE time). */
const uaeDay = (d: Date) => Math.floor((d.getTime() + UAE_OFFSET_MS) / DAY_MS);

/**
 * "Next update" text: "Tonight, 7:00 AM UAE" when the next 03:00 UTC run is within 24 h and on the next
 * UAE calendar day; "Today, 7:00 AM UAE" when it is later the same UAE day; else "{EEE} 7:00 AM UAE".
 */
export function nextUpdateLabel(now: Date): string {
  const run = nextNightlyRun(now);
  const withinDay = run.getTime() - now.getTime() <= DAY_MS;
  if (uaeDay(run) === uaeDay(now)) return "Today, 7:00 AM UAE";
  if (withinDay && uaeDay(run) === uaeDay(now) + 1) return "Tonight, 7:00 AM UAE";
  /* v8 ignore next 2 -- the next 03:00 UTC run is always within 24 h; kept for the stated rule */
  const weekday = WEEKDAYS[new Date(run.getTime() + UAE_OFFSET_MS).getUTCDay()];
  return `${weekday} 7:00 AM UAE`;
}
